import { useQuery } from '@tanstack/react-query';
import type { AssetDriveSmartResponse } from '@siliconx/shared';
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { DriveSmartTable } from '@siliconx/shared/ui/DriveSmartTable';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { formatDate } from '../../lib/format';

/**
 * The nine drive SMART measurements an asset's recent bench runs reported. Display only: values, no limits and no grade (grading on
 * SMART is a separate switch, default off). Renders nothing while loading, on error, or when no run reported any, so an asset that
 * is not a tested drive shows no empty card.
 */
export function DriveSmartCard({ assetId }: { assetId: string }) {
  const q = useQuery({
    queryKey: qk.assets.driveSmart(assetId),
    queryFn: async (): Promise<AssetDriveSmartResponse> => {
      const res = await api.api.client.assets[':assetId']['drive-smart'].$get({ param: { assetId } });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as AssetDriveSmartResponse;
    },
  });
  const runs = q.data?.runs ?? [];
  if (runs.length === 0) return null;
  return (
    <Card className="space-y-3" data-testid="asset-drive-smart">
      <PanelTitle title="Drive SMART" description="As reported by the drive during bench testing. Most recent runs first." />
      {runs.map((r) => (
        <div key={`${r.runId}-${r.deviceIndex}`} className="space-y-1">
          <p className="text-xs text-muted-foreground">Bench run {formatDate(r.runStartedAt)}</p>
          <DriveSmartTable readings={r.readings} testId={`drive-smart-${r.runId}-${r.deviceIndex}`} />
        </div>
      ))}
    </Card>
  );
}
